import { getAuth } from 'firebase/auth'
import { app } from './client'

/**
 * Calling a staff Cloud Function.
 *
 * Every request carries the signed-in user's Firebase ID token, because the
 * functions now demand one — see `functions-webhook/src/requireStaff.ts` for
 * what happened when they did not.
 *
 * `getIdToken()` returns the cached token and refreshes it only when it is
 * close to expiry, so this is not a network round trip per call.
 */
const BASE = 'https://europe-west3-skida-internal.cloudfunctions.net'

/**
 * Which gathered API serves each action — 27 Oct 2026.
 *
 * The engine's staff and shareholder endpoints became two functions routed by
 * path (`functions-webhook/src/staffApi.ts`), and the action is the old
 * function name, so callers still name what they want done and this decides
 * where it goes. A name not listed is a function of its own.
 */
const GATHERED: Record<string, 'crmStaffApi' | 'crmShareholderApi'> = {
  crmAudiencePreview: 'crmStaffApi',
  crmAutomationTestSend: 'crmStaffApi',
  crmLinkAmbassador: 'crmStaffApi',
  crmReaderFacts: 'crmStaffApi',
  crmResendNonOpeners: 'crmStaffApi',
  crmSendCampaign: 'crmStaffApi',
  crmSendPaymentRequest: 'crmStaffApi',
  crmSendShareholderMail: 'crmStaffApi',
  crmShareholderArchive: 'crmShareholderApi',
  crmShareholderFeedback: 'crmShareholderApi',
}

const urlFor = (name: string) => (GATHERED[name] ? `${BASE}/${GATHERED[name]}/${name}` : `${BASE}/${name}`)

export class FunctionError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** The whole body, for the few callers that need more than the message. */
    readonly body: unknown,
  ) {
    super(message)
  }
}

async function authHeader(): Promise<Record<string, string>> {
  const user = getAuth(app).currentUser
  if (!user) throw new FunctionError('You are signed out.', 401, null)
  return { Authorization: `Bearer ${await user.getIdToken()}` }
}

export async function callFunction<T>(
  name: string,
  init: { method?: 'GET' | 'POST'; query?: Record<string, string>; body?: unknown } = {},
): Promise<T> {
  const url = new URL(urlFor(name))
  for (const [k, v] of Object.entries(init.query ?? {})) url.searchParams.set(k, v)

  const res = await fetch(url, {
    method: init.method ?? 'GET',
    headers: {
      ...(await authHeader()),
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: init.body ? JSON.stringify(init.body) : undefined,
  })

  const body: unknown = await res.json().catch(() => null)
  if (!res.ok) {
    const message =
      (body as { error?: string } | null)?.error ?? `The request failed (${res.status}).`
    // The status is carried rather than flattened into the message: 428 means
    // "test first" and 422 means "that audience refuses that kind", and the
    // compose screen shows them differently.
    throw new FunctionError(message, res.status, body)
  }
  return body as T
}

/**
 * A function that answers with a file rather than with JSON — 18 Sep 2026.
 *
 * The shareholder archive streams its PDFs through a function, because a
 * shareholder cannot read the bucket and the collector cannot sign a URL — see
 * `shareholderArchive.ts`. That means a request that carries an ID token and
 * comes back as bytes, which `callFunction` cannot express: it parses every
 * response as JSON.
 *
 * An error still arrives as JSON, so the failure path reads the body the same
 * way and throws the same `FunctionError` the rest of the app already handles.
 */
export async function callFunctionForBlob(
  name: string,
  init: { body?: unknown } = {},
): Promise<Blob> {
  const res = await fetch(urlFor(name), {
    method: 'POST',
    headers: {
      ...(await authHeader()),
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: init.body ? JSON.stringify(init.body) : undefined,
  })

  if (!res.ok) {
    const body: unknown = await res.json().catch(() => null)
    throw new FunctionError(
      (body as { error?: string } | null)?.error ?? `The request failed (${res.status}).`,
      res.status,
      body,
    )
  }
  return res.blob()
}
